import {
  collection,
  query,
  where,
  limit,
  getDocs,
  doc,
  updateDoc,
  increment,
  arrayUnion
} from 'firebase/firestore'
import { db } from '@/firebase'
import { normCouponFromDoc } from '@/lib/couponSchema'
import { parseItemPrice } from '@/lib/restaurantPublicProfile'

/** @typedef {ReturnType<typeof normCouponFromDoc>} MenuCoupon */

export async function fetchCouponByCode(code, city) {
  const normalizedCode = String(code || '').trim().toUpperCase()
  const normalizedCity = String(city || '').trim()
  if (!normalizedCode || !normalizedCity) return null

  const snap = await getDocs(
    query(
      collection(db, 'Coupons'),
      where('code', '==', normalizedCode),
      where('where', '==', normalizedCity),
      limit(1)
    )
  )

  if (snap.empty) return null
  const d = snap.docs[0]
  return normCouponFromDoc(d.id, d.data())
}

function isBasedOnCount(coupon) {
  return coupon?.based_on_count === true
}

/**
 * @returns {{ ok: true, coupon: MenuCoupon } | { ok: false, error: string }}
 */
export function validateCouponForCheckout(coupon, { userId, restaurantId }) {
  if (!coupon) {
    return { ok: false, error: 'كود غير صالح' }
  }

  const uid = String(userId || '').trim()
  const restId = String(restaurantId || '').trim()

  if (isBasedOnCount(coupon) && (coupon.count ?? 0) <= 0) {
    return { ok: false, error: 'انتهت صلاحية الكوبون' }
  }

  if (coupon.based_on_date === true) {
    const endDate = coupon.end_date instanceof Date ? coupon.end_date : null
    if (!endDate || new Date() > endDate) {
      return { ok: false, error: 'انتهت صلاحية الكوبون' }
    }
  }

  if (uid && Array.isArray(coupon.users) && coupon.users.includes(uid)) {
    return { ok: false, error: 'استخدمت الكوبون من قبل' }
  }

  if (coupon.for_all_res === false) {
    if (!restId || !Array.isArray(coupon.restaurants) || !coupon.restaurants.includes(restId)) {
      return { ok: false, error: 'الكوبون غير صالح لهذا الكيان' }
    }
  }

  return { ok: true, coupon }
}

/** حساب قيمة الخصم — نفس منطق تطبيق الموبايل */
export function calculateMenuCouponDiscount({ appliedCoupon, itemTotal, deliveryCost }) {
  if (!appliedCoupon) return 0

  const discountPerc = parseFloat(String(appliedCoupon.discount ?? '0')) || 0
  const items = parseItemPrice(itemTotal)
  const delivery = parseItemPrice(deliveryCost)
  let calculated = 0

  if (appliedCoupon.offer_in_total === true) {
    calculated = (items + delivery) * (discountPerc / 100)
  } else {
    calculated = delivery * (discountPerc / 100)
  }

  const uptoRaw = appliedCoupon.upto
  if (uptoRaw != null && uptoRaw !== '') {
    const upto =
      typeof uptoRaw === 'string' ? parseFloat(uptoRaw) : Number(uptoRaw)
    if (Number.isFinite(upto) && calculated > upto) {
      calculated = upto
    }
  }

  return Math.max(0, calculated)
}

export async function applyMenuCoupon(code, { city, userId, restaurantId }) {
  const coupon = await fetchCouponByCode(code, city)
  if (!coupon) {
    return { ok: false, error: 'كود غير صالح' }
  }
  return validateCouponForCheckout(coupon, { userId, restaurantId })
}

export async function consumeCouponAfterOrder(couponId, userId) {
  const id = String(couponId || '').trim()
  const uid = String(userId || '').trim()
  if (!id || !uid) return

  await updateDoc(doc(db, 'Coupons', id), {
    count: increment(-1),
    users: arrayUnion(uid)
  })
}
